import {
  useQuery,
  useMutation,
  createConnectQueryKey,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import {
  listSystemEmailTemplates,
  getSystemEmailTemplate,
  updateSystemEmailTemplate,
  resetSystemEmailTemplate,
  previewSystemEmailTemplate,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListSystemEmailTemplatesRequestSchema,
  ListSystemEmailTemplatesResponseSchema,
  GetSystemEmailTemplateRequestSchema,
  GetSystemEmailTemplateResponseSchema,
  SystemEmailTemplateType,
} from '@/gen/cortado/v1/emails_pb'
import { authStore } from './auth.store'

/**
 * Hook to fetch all system email templates for an organization
 */
export function useSystemEmailTemplates(organizationId: string) {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    listSystemEmailTemplates,
    create(ListSystemEmailTemplatesRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && !!organizationId,
    }
  )

  return {
    templates: query.data?.templates || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a specific system email template
 */
export function useSystemEmailTemplate(
  organizationId: string,
  templateType: SystemEmailTemplateType
) {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    getSystemEmailTemplate,
    create(GetSystemEmailTemplateRequestSchema, { organizationId, templateType }),
    {
      enabled: isAuthenticated && !!organizationId && templateType !== SystemEmailTemplateType.UNSPECIFIED,
    }
  )

  return {
    template: query.data?.template || null,
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to update a system email template
 */
export function useUpdateSystemEmailTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateSystemEmailTemplate, {
    onSuccess: (response, variables) => {
      const updatedTemplate = response.template
      if (!updatedTemplate) return

      // Update specific template cache
      const templateQueryKey = createConnectQueryKey({
        schema: getSystemEmailTemplate,
        transport,
        input: {
          organizationId: variables.organizationId,
          templateType: variables.templateType,
        },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        templateQueryKey,
        create(GetSystemEmailTemplateResponseSchema, { template: updatedTemplate })
      )

      // Update list cache
      const listQueryKey = createConnectQueryKey({
        schema: listSystemEmailTemplates,
        transport,
        input: { organizationId: variables.organizationId },
        cardinality: 'finite',
      })

      const existingList = queryClient.getQueryData<{ templates: typeof updatedTemplate[] }>(listQueryKey)

      if (existingList?.templates) {
        const updatedTemplates = existingList.templates.map((template) =>
          template.templateType === updatedTemplate.templateType ? updatedTemplate : template
        )

        queryClient.setQueryData(
          listQueryKey,
          create(ListSystemEmailTemplatesResponseSchema, { templates: updatedTemplates })
        )
      }

      // Background refetch for consistency
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSystemEmailTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to reset a system email template to defaults
 */
export function useResetSystemEmailTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(resetSystemEmailTemplate, {
    onSuccess: (response, variables) => {
      const resetTemplate = response.template
      if (!resetTemplate) return

      // Update specific template cache
      const templateQueryKey = createConnectQueryKey({
        schema: getSystemEmailTemplate,
        transport,
        input: {
          organizationId: variables.organizationId,
          templateType: variables.templateType,
        },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        templateQueryKey,
        create(GetSystemEmailTemplateResponseSchema, { template: resetTemplate })
      )

      // Update list cache
      const listQueryKey = createConnectQueryKey({
        schema: listSystemEmailTemplates,
        transport,
        input: { organizationId: variables.organizationId },
        cardinality: 'finite',
      })

      const existingList = queryClient.getQueryData<{ templates: typeof resetTemplate[] }>(listQueryKey)

      if (existingList?.templates) {
        const updatedTemplates = existingList.templates.map((template) =>
          template.templateType === resetTemplate.templateType ? resetTemplate : template
        )

        queryClient.setQueryData(
          listQueryKey,
          create(ListSystemEmailTemplatesResponseSchema, { templates: updatedTemplates })
        )
      }

      // Background refetch for consistency
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSystemEmailTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to preview a system email template with sample data
 */
export function usePreviewSystemEmailTemplate() {
  const mutation = useMutation(previewSystemEmailTemplate)

  return {
    mutation,
    preview: mutation.data
      ? {
          renderedSubject: mutation.data.renderedSubject,
          renderedHtml: mutation.data.renderedHtml,
        }
      : null,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  }
}

/**
 * Get display name for a system email template type
 */
export function getTemplateTypeDisplayName(templateType: SystemEmailTemplateType): string {
  switch (templateType) {
    case SystemEmailTemplateType.NEW_INQUIRY:
      return 'New Inquiry Confirmation'
    case SystemEmailTemplateType.QUOTE_SENT:
      return 'Quote Sent'
    case SystemEmailTemplateType.INVOICE_SENT:
      return 'Invoice Sent'
    case SystemEmailTemplateType.PAYMENT_RECEIPT:
      return 'Payment Receipt'
    case SystemEmailTemplateType.PAYMENT_PROCESSING:
      return 'Payment Processing'
    case SystemEmailTemplateType.PAYMENT_FAILED:
      return 'Payment Failed'
    case SystemEmailTemplateType.REFUND_PROCESSED:
      return 'Refund Processed'
    case SystemEmailTemplateType.INBOX_NEW_THREAD_NOTIFICATION:
      return 'Inbox New Thread'
    case SystemEmailTemplateType.INBOX_REPLY_NOTIFICATION:
      return 'Inbox Reply'
    case SystemEmailTemplateType.REPLY_CONFIRMATION:
      return 'Reply Confirmation'
    case SystemEmailTemplateType.RESEND_LINK:
      return 'Resend Portal Link'
    case SystemEmailTemplateType.ACH_VERIFICATION_REQUIRED:
      return 'ACH Verification Required'
    case SystemEmailTemplateType.REFUND_FAILED:
      return 'Refund Failed'
    default:
      return 'Unknown'
  }
}

/**
 * Get description for a system email template type
 */
export function getTemplateTypeDescription(templateType: SystemEmailTemplateType): string {
  switch (templateType) {
    case SystemEmailTemplateType.NEW_INQUIRY:
      return 'Confirmation email sent to customers after they submit an inquiry'
    case SystemEmailTemplateType.QUOTE_SENT:
      return 'Email sent to customers when a quote is shared with them'
    case SystemEmailTemplateType.INVOICE_SENT:
      return 'Email sent to customers when an invoice is shared with them'
    case SystemEmailTemplateType.PAYMENT_RECEIPT:
      return 'Receipt email sent to customers after a successful payment'
    case SystemEmailTemplateType.PAYMENT_PROCESSING:
      return 'Email sent when an ACH payment is being processed'
    case SystemEmailTemplateType.PAYMENT_FAILED:
      return 'Email sent when a payment attempt fails'
    case SystemEmailTemplateType.REFUND_PROCESSED:
      return 'Email sent when a refund has been processed'
    case SystemEmailTemplateType.INBOX_NEW_THREAD_NOTIFICATION:
      return 'Notification sent when a new shared inbox thread arrives'
    case SystemEmailTemplateType.INBOX_REPLY_NOTIFICATION:
      return 'Notification sent when a shared inbox thread receives a reply'
    case SystemEmailTemplateType.REPLY_CONFIRMATION:
      return 'Confirmation sent after a reply-by-email response is delivered'
    case SystemEmailTemplateType.RESEND_LINK:
      return 'Email sent when a customer requests a fresh portal link'
    case SystemEmailTemplateType.ACH_VERIFICATION_REQUIRED:
      return 'Email sent when a customer must verify their bank account to complete an ACH payment'
    case SystemEmailTemplateType.REFUND_FAILED:
      return 'Email sent when a previously initiated refund could not be completed'
    default:
      return ''
  }
}
